import { useEffect, useRef, useState } from "react";
import { Clock, Crown, ShieldCheck, Zap } from "lucide-react";
import { useApp } from "../context/AppContext";
import { SAR } from "../souq";

const pad = (n) => String(n).padStart(2, "0");

export function useTick(interval = 1000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const i = setInterval(() => setNow(Date.now()), interval); return () => clearInterval(i); }, [interval]);
  return now;
}

/* Segmented D/H/M/S countdown — tabular digits, urgency < 1h, RTL-safe (ltr inner). */
export function Countdown({ endsAt, size = "sm", testId }) {
  const { t } = useApp();
  const now = useTick(1000);
  const ms = new Date(endsAt).getTime() - now;
  if (ms <= 0) return <span className={`cd cd-${size} cd-ended`} data-testid={testId}><Clock size={12} /> {t("souq.ended")}</span>;
  const s = Math.floor(ms / 1000), d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const urgent = ms < 3600_000;
  const units = [["d", d], ["h", h], ["m", m], ["s", sec]].filter(([u]) => d > 0 || u !== "d");
  return (
    <span className={`cd cd-${size} ${urgent ? "cd-urgent" : ""}`} data-testid={testId} data-urgent={urgent} aria-label={t("souq.timeLeft")}>
      <Clock size={size === "lg" ? 14 : 12} className="cd-ic" />
      <span className="cd-units" dir="ltr">
        {units.map(([u, v]) => (
          <span key={u} className="cd-unit">
            <span className="cd-val" key={`${u}-${v}`}>{u === "d" ? v : pad(v)}</span>
            <span className="cd-lbl">{t(`souq.u.${u}`)}</span>
          </span>
        ))}
      </span>
    </span>
  );
}

export function relTime(iso, now, t) {
  const s = Math.max(0, Math.floor((now - new Date(iso).getTime()) / 1000));
  if (s < 60) return t("rt.now");
  const m = Math.floor(s / 60); if (m < 60) return t("rt.m").replace("{n}", m);
  const h = Math.floor(m / 60); if (h < 24) return t("rt.h").replace("{n}", h);
  return t("rt.d").replace("{n}", Math.floor(h / 24));
}

/* Animated price: flashes gold when it changes. */
export function LivePrice({ value, lang, className = "", testId }) {
  const [flash, setFlash] = useState(false);
  const prev = useRef(value);
  useEffect(() => {
    if (prev.current !== value) { prev.current = value; setFlash(true); const to = setTimeout(() => setFlash(false), 900); return () => clearTimeout(to); }
  }, [value]);
  return <strong className={`${className} live-price ${flash ? "flash" : ""}`} dir="ltr" data-testid={testId}>{SAR(value, lang)}</strong>;
}

/* Bid history timeline — highest bid crowned, new rows animate in. */
export function BidHistory({ bids, lang }) {
  const { t } = useApp();
  const now = useTick(30000);
  const seen = useRef(new Set(bids.map((b) => b.bid_id || `${b.amount}-${b.created_at}`)));
  const [fresh, setFresh] = useState(new Set());
  useEffect(() => {
    const ids = bids.map((b) => b.bid_id || `${b.amount}-${b.created_at}`);
    const add = ids.filter((id) => !seen.current.has(id));
    if (add.length) { add.forEach((id) => seen.current.add(id)); setFresh(new Set(add)); const to = setTimeout(() => setFresh(new Set()), 1200); return () => clearTimeout(to); }
  }, [bids]);
  if (!bids.length) return <p className="dash-empty">{t("souq.noBids")}</p>;
  return (
    <ol className="bh-list" data-testid="bid-history-list">
      {bids.map((b, i) => {
        const id = b.bid_id || `${b.amount}-${b.created_at}`;
        return (
          <li key={id} className={`bh-row ${i === 0 ? "top" : ""} ${fresh.has(id) ? "bh-new" : ""}`} data-testid={`bid-row-${i}`}>
            <span className="bh-avatar" aria-hidden>{b.bidder_initial || b.bidder_masked?.[0] || "?"}</span>
            <span className="bh-main">
              <span className="bh-name">{b.bidder_masked}
                {i === 0 && <em className="bh-crown" title={t("souq.highest")}><Crown size={11} /> {t("souq.highest")}</em>}
                {b.via === "buy_now" && <em className="souq-buynow-chip"><Zap size={10} /> {t("souq.buyNow")}</em>}
              </span>
              <time className="bh-time" dateTime={b.created_at}>{relTime(b.created_at, now, t)}</time>
            </span>
            <strong className="bh-amount" dir="ltr">{SAR(b.amount, lang)}</strong>
          </li>
        );
      })}
    </ol>
  );
}

/* Silver "Verified Trader" mark — distinct from the gold Watheeq badge. */
export function VerifiedTraderBadge({ size = "sm", testId }) {
  const { t } = useApp();
  return (
    <span className={`vt-badge vt-${size}`} title={t("vt.hint")} data-testid={testId}>
      <ShieldCheck size={size === "lg" ? 14 : 11} /> {t("vt.badge")}
    </span>
  );
}
